import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractErr, mediaUrl, storePublicBase, API } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Upload, FileDown, Eye, Download, Loader2, ImageIcon, Sparkles, CheckCircle2, Copy, Mic } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

import { useTranslation } from "react-i18next";
import { listingImageUrl } from "@/lib/productGallery";
import TutorialBanner, { SECTION_TITLES, useSectionTutorials } from "@/components/TutorialBanner";
import { ASM_STORE_CHANGED_EVENT, ASM_TALK_EVENT } from "@/components/AIStoreManagerDock";
import "./aiStoreManager.css";

export default function AdminProducts() {
  const { t } = useTranslation();
  const { tenant } = useMerchantAuth();
  const nav = useNavigate();
  const sectionTutorials = useSectionTutorials();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkCategory, setBulkCategory] = useState("__keep");
  const [bulkStatus, setBulkStatus] = useState("__keep");
  const [exporting, setExporting] = useState(false);

  const [quota, setQuota] = useState(null);

  const [duplicating, setDuplicating] = useState(null);
  const [asmOn, setAsmOn] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/merchant/products").then(({ data }) => setItems(data)),
      api.get("/merchant/categories").then(({ data }) => setCats(data || [])).catch(() => {}),
      api.get("/merchant/product-quota").then(({ data }) => setQuota(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/merchant/ai-store-manager/status")
      .then(({ data }) => setAsmOn(Boolean(data?.on && data?.active)))
      .catch(() => setAsmOn(false));
  }, []);
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener(ASM_STORE_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(ASM_STORE_CHANGED_EVENT, onChanged);
  }, []);

  const del = async (id) => {
    try {
      await api.delete(`/merchant/products/${id}`);
      toast.success("Product deleted");
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const duplicate = async (id) => {
    if (atLimit) { toast.error(limitWarning); return; }
    setDuplicating(id);
    try {
      const { data } = await api.post(`/merchant/products/${id}/duplicate`);
      toast.success("Product duplicated as a draft");
      nav(`/admin/products/${data.id}`);
    } catch (e) { toast.error(extractErr(e)); } finally { setDuplicating(null); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null);
    setImportProgress({ message: "Uploading CSV…", pct: 4, log: ["Uploading spreadsheet"], name: "" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/merchant/products/import?stream=1`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        let detail = `Import failed (${res.status})`;
        try {
          const j = await res.json();
          if (typeof j?.detail === "string") detail = j.detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let last = null;
      const pushEv = (ev) => {
        last = ev;
        const total = ev.total || ev.total_rows || 0;
        const idx = ev.index || 0;
        let pct = 8;
        if (ev.type === "start") pct = 10;
        else if (total) pct = Math.min(96, 12 + Math.round((idx / total) * 80));
        if (ev.type === "done") pct = 100;
        setImportProgress((p) => {
          const log = [...(p?.log || [])];
          if (ev.message && log[log.length - 1] !== ev.message) {
            log.push(ev.message);
            if (log.length > 8) log.shift();
          }
          return { message: ev.message || p?.message || "Working…", pct, log, name: ev.name || p?.name || "" };
        });
      };
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.trim()) continue;
          try { pushEv(JSON.parse(line)); } catch { /* skip partial */ }
        }
      }
      if (buf.trim()) {
        try { pushEv(JSON.parse(buf)); } catch { /* ignore */ }
      }
      const data = last && last.type === "done" ? last : { created: 0, updated: 0, errors: [] };
      setImportResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e?.message || extractErr(e));
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const sampleCsv = "name,description,price,mrp,stock,sku,category_slug,category_name,is_active,is_featured,image_url\nWhite Shirt,\"Cotton white shirt\",499,799,10,SH-001,shirts,Shirts,true,true,\nBlue Shirt,\"Cotton blue shirt\",499,799,8,SH-002,shirts,Shirts,true,false,\nNew Cap,\"Wool cap\",299,399,15,CAP-01,caps,Caps,true,false,\n";
  const downloadSample = () => {
    const blob = new Blob([sampleCsv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "products-sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doExport = async (ids) => {
    setExporting(true);
    try {
      const params = ids && ids.length ? { ids: ids.join(",") } : {};
      const { data } = await api.get("/merchant/products/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "products-export.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${ids && ids.length ? ids.length : items.length} product${(ids && ids.length ? ids.length : items.length) === 1 ? "" : "s"}`);
    } catch (e) { toast.error(extractErr(e)); } finally { setExporting(false); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/merchant/products/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} product${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setBulkDeleting(false); }
  };

  const bulkUpdate = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const payload = { ids };
    if (bulkCategory === "__none") payload.clear_category = true;
    else if (bulkCategory !== "__keep") payload.category_id = bulkCategory;
    if (bulkStatus === "live") payload.is_active = true;
    else if (bulkStatus === "draft") payload.is_active = false;
    if (!payload.clear_category && !payload.category_id && payload.is_active === undefined) {
      toast.error("Choose a category or status to update");
      return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/merchant/products/bulk-update", payload);
      toast.success(`Updated ${data.updated} product${data.updated === 1 ? "" : "s"}`);
      setBulkOpen(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setBulkSaving(false); }
  };

  const filtered = items.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const someSelected = filtered.some((p) => selected.has(p.id));

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filtered.forEach((p) => n.delete(p.id));
    else filtered.forEach((p) => n.add(p.id));
    return n;
  });

  const talkToAdd = () => {
    if (!asmOn) return;
    window.dispatchEvent(new CustomEvent(ASM_TALK_EVENT, { detail: { focus: "products" } }));
  };
  const viewUrl = (p) => `${storePublicBase(tenant)}/product/${p.slug}`;
  const atLimit = quota && quota.remaining <= 0;
  const limitWarning = quota ? `posting limit ${quota.product_limit}products reached, contact support to increase rate limit` : "";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("products.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">
            {items.length} product{items.length === 1 ? "" : "s"}
            {quota ? <span className="font-mono-nums"> · {quota.product_count}/{quota.product_limit} posting limit</span> : null}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => doExport(null)} disabled={exporting || items.length === 0} data-testid="products-export-all-btn" className="rounded-full"><Download className="w-4 h-4 mr-1" /> {exporting ? "Exporting…" : t("products.exportCsv")}</Button>
          <Dialog open={importOpen} onOpenChange={(v) => { if (importing) return; setImportOpen(v); if (!v) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="products-import-btn" className="rounded-full"><Upload className="w-4 h-4 mr-1" /> {t("products.importCsv")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk import products (CSV)</DialogTitle>
                <DialogDescription>
                  Required columns: <code className="font-mono-nums text-xs">name, price, stock</code>. Optional: <code className="font-mono-nums text-xs">description, mrp, sku, category_slug, is_active, is_featured, image_url</code>.
                  Image links are downloaded, converted to WebP, and saved on our servers — we never keep the original CSV link. Existing products with a matching slug are updated. Categories in <code className="font-mono text-xs">category_slug</code> or <code className="font-mono text-xs">category_name</code> are created if missing, or linked if they already exist.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Button variant="outline" onClick={downloadSample} data-testid="import-sample" className="w-full"><FileDown className="w-4 h-4 mr-1" /> Download sample CSV</Button>
                <label className="block">
                  <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30">
                    <Upload className="w-6 h-6 mx-auto text-slate-400" />
                    <div className="mt-2 text-sm text-slate-600">{importing ? "Importing… please wait" : "Click to upload CSV"}</div>
                  </div>
                  <input type="file" hidden accept=".csv,text/csv" data-testid="import-file" disabled={importing} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; f && doImport(f); }} />
                </label>
                {importResult && (
                  <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="font-mono-nums"><span className="text-emerald-700 font-semibold">{importResult.created}</span> created · <span className="text-blue-700 font-semibold">{importResult.updated}</span> updated · <span className={importResult.errors?.length ? "text-red-700" : ""}>{importResult.errors?.length || 0} errors</span>{importResult.images_ok != null ? <span> · {importResult.images_ok} images saved</span> : null}</div>
                    {importResult.errors?.length > 0 && (
                      <ul className="mt-2 text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                        {importResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setImportOpen(false)}>Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {asmOn ? (
          <button type="button" onClick={talkToAdd} data-testid="products-talk-ai-btn" className="asm-products-talk" aria-label="Add product by talking to AI">
            <span className="asm-products-talk-icon" aria-hidden="true">
              <span className="asm-products-talk-ring" />
              <Mic className="w-4 h-4" />
            </span>
            {t("products.talkToAdd", "Add by talking")}
          </button>
          ) : null}
          <Link to={atLimit ? "#" : "/admin/products/new"} data-testid="products-new-btn" onClick={(e) => { if (atLimit) { e.preventDefault(); toast.error(limitWarning); } }}>
            <Button disabled={atLimit} className="rounded-full bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> {t("products.newProduct")}</Button>
          </Link>
        </div>
      </div>

      <TutorialBanner section="products" url={sectionTutorials.products} title={SECTION_TITLES.products} testId="tutorial-banner-products" />

      {importing && importProgress && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm" data-testid="csv-import-progress">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-100">
                <Sparkles className="w-4 h-4" /> CSV import
              </div>
              <h2 className="font-display text-xl font-bold mt-1">Importing products</h2>
              <p className="text-sm text-blue-100 mt-1">Please wait — images are downloaded, converted to WebP, and uploaded to our servers.</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300" style={{ width: `${importProgress.pct || 8}%` }} />
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{importProgress.message}</div>
                  {importProgress.name ? <div className="text-xs text-slate-500 mt-0.5 truncate">{importProgress.name}</div> : null}
                </div>
              </div>
              <ol className="space-y-1.5 text-xs text-slate-500 max-h-36 overflow-hidden">
                {(importProgress.log || []).map((line, i) => (
                  <li key={`${i}-${line}`} className="flex items-start gap-2">
                    {i === (importProgress.log.length - 1)
                      ? <Loader2 className="w-3.5 h-3.5 mt-0.5 animate-spin text-blue-500 shrink-0" />
                      : <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" />}
                    <span className={i === importProgress.log.length - 1 ? "text-slate-800" : ""}>{line}</span>
                  </li>
                ))}
              </ol>
              <div className="flex items-center gap-2 text-[11px] text-slate-400">
                <ImageIcon className="w-3.5 h-3.5" />
                External links are not kept. Each photo is stored as WebP on our CDN.
              </div>
            </div>
          </div>
        </div>
      )}

      {atLimit && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="products-limit-warning">
          {limitWarning}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="products-search" placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3" data-testid="products-bulk-bar">
          <div className="text-sm font-medium text-slate-700"><span data-testid="products-selected-count">{selected.size}</span> selected</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full bg-white" disabled={exporting} onClick={() => doExport(Array.from(selected))} data-testid="products-export-selected-btn"><Download className="w-4 h-4 mr-1" /> Export selected</Button>
            <Button variant="outline" size="sm" className="rounded-full bg-white" onClick={() => { setBulkCategory("__keep"); setBulkStatus("__keep"); setBulkOpen(true); }} data-testid="products-bulk-edit-btn">Bulk edit</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full bg-white text-red-600 hover:text-red-700 border-red-200" data-testid="products-bulk-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete selected</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {selected.size} product{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                  <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={bulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700" data-testid="products-bulk-delete-confirm">{bulkDeleting ? "Deleting…" : "Delete"}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSelected(new Set())} data-testid="products-clear-selection">Clear</Button>
          </div>
        </div>
      )}

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-slate-500 text-sm">No products yet.</div>
            <Link to="/admin/products/new"><Button className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="products-empty-cta"><Plus className="w-4 h-4 mr-1" /> Add your first product</Button></Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* Header row with select-all */}
            <div className="px-4 py-2.5 flex items-center gap-4 bg-slate-50/70 text-xs font-medium text-slate-500">
              <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)} onCheckedChange={toggleAll} data-testid="products-select-all" aria-label="Select all" />
              <span>Select all</span>
            </div>
            {filtered.map((p) => (
              <div key={p.id} className={`p-4 grid grid-cols-[auto_auto_minmax(0,1fr)] sm:grid-cols-[auto_auto_minmax(0,1fr)_auto] items-start sm:items-center gap-x-3 gap-y-3 sm:gap-4 hover:bg-slate-50 ${selected.has(p.id) ? "bg-blue-50/40" : ""}`} data-testid={`product-row-${p.id}`}>
                <Checkbox className="mt-1 sm:mt-0" checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`product-select-${p.id}`} aria-label={`Select ${p.name}`} />
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                  {listingImageUrl(p.images) ? <img src={mediaUrl(listingImageUrl(p.images))} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">No img</div>}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 leading-snug line-clamp-2 break-words">{p.name}</div>
                  <div className="text-xs text-slate-500 flex gap-3 mt-0.5 flex-wrap">
                    <span className="font-mono-nums">SKU {p.sku || "—"}</span>
                    <span className={`font-mono-nums ${p.stock === 0 ? "text-red-600" : "text-slate-600"}`}>
                      {p.stock === null || p.stock === undefined ? "Unlimited stock" : `Stock ${p.stock}`}
                    </span>
                    {p.variants?.length > 0 && <span className="font-mono-nums text-slate-500">{p.variants.length} variant{p.variants.length === 1 ? "" : "s"}</span>}
                    {!p.is_active && <Badge variant="secondary">Draft</Badge>}
                    {p.is_featured && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Featured</Badge>}
                    {p.is_digital && <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">Digital</Badge>}
                  </div>
                </div>
                <div className="col-span-3 sm:col-span-1 flex items-center justify-end gap-3 shrink-0 sm:pl-2">
                <div className="font-mono-nums text-slate-900 font-semibold shrink-0">₹{p.price.toLocaleString("en-IN")}</div>
                <div className="flex gap-2 shrink-0">
                  <a href={viewUrl(p)} target="_blank" rel="noopener noreferrer" title="View on storefront">
                    <Button variant="outline" size="sm" data-testid={`product-view-${p.id}`}><Eye className="w-4 h-4" /></Button>
                  </a>
                  <Link to={`/admin/products/${p.id}`}>
                    <Button variant="outline" size="sm" data-testid={`product-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  </Link>
                  <Button variant="outline" size="sm" title="Duplicate" disabled={duplicating === p.id || atLimit} onClick={() => duplicate(p.id)} data-testid={`product-duplicate-${p.id}`}><Copy className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid={`product-delete-${p.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this product?</AlertDialogTitle>
                        <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(p.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${p.id}`}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk edit {selected.size} product{selected.size === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>Leave a field on “Keep current” to skip it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="bulk-category" value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}>
                <option value="__keep">Keep current</option>
                <option value="__none">No category</option>
                {cats.filter((c) => !c.parent_id).flatMap((c) => [
                  <option key={c.id} value={c.id}>{c.name}</option>,
                  ...cats.filter((ch) => ch.parent_id === c.id).map((ch) => (
                    <option key={ch.id} value={ch.id}>— {ch.name}</option>
                  )),
                ])}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="bulk-status" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                <option value="__keep">Keep current</option>
                <option value="live">Live (visible in store)</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkUpdate} disabled={bulkSaving} data-testid="bulk-edit-save" className="bg-blue-600 hover:bg-blue-700">{bulkSaving ? "Saving…" : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
